import React from 'react'
import { makeStyles, Box, Grid } from '@material-ui/core'
import { SidePanel, Typography } from 'src/modules/ui'
import { generateExtraChips, mapRecipeChipKeyToLabel } from '../utils'
import { formatPrice } from '../../../../../utils'

const useStyles = makeStyles(theme => ({
  sidePanelContainer: {
    flexShrink: 0,
    marginLeft: 0,
    marginTop: theme.spacing(4),
    width: 'auto',
    [theme.breakpoints.up('desktop')]: {
      marginTop: theme.spacing(9),
      marginLeft: theme.spacing(4),
      width: 250,
    },
  },
  sidePanelGroup: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(2),
  },
}))

const RecipeSummarySidePanel = ({ children, recipe }) => {
  const classes = useStyles()

  const contains = recipe.allergens?.contains ? recipe.allergens?.contains : []
  const mayContain = recipe.allergens?.mayContain
    ? recipe.allergens?.mayContain
    : []

  const hasAllergens = contains.length > 0 || mayContain.length > 0

  const extraChips = generateExtraChips(recipe)

  const calculateRecipeTotal = recipe => {
    const ingredientCost = recipe.ingredientsCost.value
      ? +recipe.ingredientsCost.value
      : 0
    const labourCosts = recipe.labourCosts ? +recipe.labourCosts : 0
    const equipmentCosts = recipe.equipmentCosts ? +recipe.equipmentCosts : 0

    return ingredientCost + labourCosts + equipmentCosts
  }

  const cannotCaclulateErrors = []

  if (recipe && recipe.recipeType === 'finished_recipe') {
    if (!recipe.batchSize)
      cannotCaclulateErrors.push(
        'Batch Size must be set to calculate GP % and TGP %'
      )
    if (!recipe.menuPrice)
      cannotCaclulateErrors.push(
        'Menu price must be set to calculate GP % and TGP %'
      )
  }

  return (
    <div className={classes.sidePanelContainer}>
      <SidePanel>
        <div className={classes.sidePanelGroup}>
          <div>
            <Typography variant="h5">Summary</Typography>

            <Box
              component="img"
              src={recipe.photo}
              style={{
                marginTop: '0.6rem',
                marginBottom: '2rem',
                width: '150px',
              }}
            />
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <Typography variant="h5">Categories</Typography>
                {recipe.categories && recipe.categories.length > 0 ? (
                  recipe.categories.map(category => (
                    <Typography variant="body2">
                      {category.category.name}
                    </Typography>
                  ))
                ) : (
                  <Typography variant="body2">Uncategorised</Typography>
                )}
              </Grid>
              <Grid item>
                <Typography variant="h5">Type</Typography>
                <Typography variant="body2">
                  {recipe.recipeType === 'finished_recipe'
                    ? 'Recipe'
                    : 'Ingredient'}
                </Typography>
              </Grid>
              {recipe.batchPortions ? (
                <Grid item>
                  <Typography variant="h5">Number of Portions</Typography>
                  <Typography variant="body2">
                    {recipe.batchPortions}
                  </Typography>
                </Grid>
              ) : null}
              {cannotCaclulateErrors ? (
                <Grid item>
                  {cannotCaclulateErrors.map(error => () => {
                    return (
                      <Typography variant="body2" color="orange">
                        {error}
                      </Typography>
                    )
                  })}
                </Grid>
              ) : null}
              {recipe.batchSize ? (
                <Grid item>
                  <Typography variant="h5">Batch Size</Typography>
                  <Typography variant="body2">
                    {recipe.batchSize}
                    {recipe.unit === 'grams' ? 'g' : recipe.unit}
                  </Typography>
                </Grid>
              ) : null}
              {recipe.menuPrice ? (
                <Grid item>
                  <Typography variant="h5">Menu Price</Typography>
                  <Typography variant="body2">£{recipe.menuPrice}</Typography>
                </Grid>
              ) : null}
              {recipe.grossProfitPercentage?.value ? (
                <Grid item>
                  <Typography variant="h5">GP %</Typography>
                  <Typography variant="body2">
                    {recipe.grossProfitPercentage.value}%
                  </Typography>
                </Grid>
              ) : null}
              {recipe.totalGrossProfitPercentage?.value ? (
                <Grid item>
                  <Typography variant="h5">TGP %</Typography>
                  <Typography variant="body2">
                    {recipe.totalGrossProfitPercentage.value}%
                  </Typography>
                </Grid>
              ) : null}
              <Grid item>
                {calculateRecipeTotal(recipe) > 0 ? (
                  <>
                    <Typography variant="h5">Costs Breakdown</Typography>
                    <Grid container spacing={1}>
                      {recipe.ingredientsCost.value ? (
                        <>
                          <Grid item xs={6}>
                            Ingredients
                          </Grid>
                          <Grid item xs={6}>
                            {formatPrice(recipe.ingredientsCost.value)}
                          </Grid>
                        </>
                      ) : null}
                      {recipe.labourCosts ? (
                        <>
                          <Grid item xs={6}>
                            Labour
                          </Grid>
                          <Grid item xs={6}>
                            {formatPrice(recipe.labourCosts)}
                          </Grid>
                        </>
                      ) : null}
                      {recipe.equipmentCosts ? (
                        <>
                          <Grid item xs={6}>
                            Equipment
                          </Grid>
                          <Grid item xs={6}>
                            {formatPrice(recipe.equipmentCosts)}
                          </Grid>
                        </>
                      ) : null}
                      <Grid item xs={6}>
                        <b>Total</b>
                      </Grid>
                      <Grid item xs={6}>
                        <b>{formatPrice(calculateRecipeTotal(recipe))}</b>
                      </Grid>
                    </Grid>
                  </>
                ) : null}
              </Grid>

              {hasAllergens ? (
                <Grid item>
                  <Typography variant="h5">Allergen Info</Typography>
                  {contains.length > 0 ? (
                    <Typography variant="body2">
                      Contains: {contains.join(', ')}
                    </Typography>
                  ) : null}
                  {mayContain.length > 0 ? (
                    <Typography variant="body2">
                      May Contain: {mayContain.join(', ')}
                    </Typography>
                  ) : null}
                </Grid>
              ) : null}

              {recipe.nutritionalInfo ? (
                <>
                  <Grid item>
                    <Typography variant="h5">Nutritional Info</Typography>
                    <Grid container spacing={1}>
                      {Object.entries(recipe.nutritionalInfo).map(info => (
                        <>
                          <Grid item xs={6}>
                            {mapRecipeChipKeyToLabel(info[0], false)}
                          </Grid>
                          <Grid item xs={6}>
                            {Math.trunc(info[1].value)}
                            {info[1].unit}
                          </Grid>
                        </>
                      ))}
                    </Grid>
                  </Grid>
                </>
              ) : null}
              {extraChips && extraChips.length > 0 ? (
                <>
                  <Grid item>
                    <Typography variant="h5">Additional Info</Typography>
                    <Grid container spacing={1}>
                      {Object.entries(extraChips).map(info => (
                        <>
                          <Grid item xs={6}>
                            {mapRecipeChipKeyToLabel(info[0], false)}
                          </Grid>
                          <Grid item xs={6}>
                            {info[1].value}
                            {info[1].unit}
                          </Grid>
                        </>
                      ))}
                    </Grid>
                  </Grid>
                </>
              ) : null}
              {recipe.carbonEmissions ? (
                <Grid item>
                  <Typography variant="h5">Carbon Emissions</Typography>
                  <Typography variant="body2">
                    {recipe.carbonEmissions}kg CO2e
                  </Typography>
                </Grid>
              ) : null}
            </Grid>
          </div>
        </div>

        {children}
      </SidePanel>
    </div>
  )
}

export default RecipeSummarySidePanel
